// Internal Imports
import DevicePlatform from '@linc-technologies/linced-common/enums/device-platform';
import StorageKey from '@linc-technologies/linced-common/enums/storage-key';

// DeviceInfo is the blob native apps write to local storage.
export interface DeviceInfo {
  physicalId: string;
  [key: string]: unknown;
}

/**
 * myDeviceInfo stores a module global to avoid having to check local storage
 * everytime for the current device's info, assuming we are on a native mobile
 * device app.
 *
 * @type {Object}
 */
let myDeviceInfo: DeviceInfo | null | undefined = null;
/**
 * myPlatform stores a module global to avoid having to check local storage
 * everytime for the platform we are on.
 *
 * @type {DevicePlatform}
 */
let myPlatform: number = DevicePlatform.UNSPECIFIED;

/**
 * getPlatform returns the platform if passed in from a native device, otherwise
 * returns `DevicePlatform.UNSPECIFIED`.
 *
 * @returns {DevicePlatform}
 */
function getPlatform(): number {
  // If we've already processed the platform, avoid using local storage.
  if (myPlatform !== DevicePlatform.UNSPECIFIED) {
    return myPlatform;
  }

  // Obtain the platform as injected by native apps.
  const platform = localStorage.getItem(StorageKey.PLATFORM);
  if (platform == null) {
    return DevicePlatform.UNSPECIFIED;
  }

  // Cast platform from a string to a number - everything from local storage is
  // stored as a string.
  myPlatform = Number(platform);

  return myPlatform;
}

// resetPlatform is used for testing purposes.
export function resetPlatform() {
  myPlatform = DevicePlatform.UNSPECIFIED;
  myDeviceInfo = null;
}

// getDeviceInfo returns the blob of data stored by devices.
export function getDeviceInfo(): DeviceInfo | null | undefined {
  // If we've already processed the device info, avoid using local storage.
  if (myDeviceInfo) {
    return myDeviceInfo;
  }

  let raw: string | null = null;
  const platform = getPlatform();
  switch (platform) {
    case DevicePlatform.IOS:
      raw =
        localStorage.getItem(StorageKey.IOS_DATA) ||
        localStorage.getItem(StorageKey.OLD_IOS_DATA);
      break;

    case DevicePlatform.ANDROID:
      raw =
        localStorage.getItem(StorageKey.ANDROID_DATA) ||
        localStorage.getItem(StorageKey.OLD_ANDROID_DATA);
      break;

    case DevicePlatform.ANDROID_TWA:
      raw = localStorage.getItem(StorageKey.ANDROID_TWA_DATA);
      break;

    default:
      return null;
  }

  let data: DeviceInfo | undefined;
  try {
    // A missing key parses as JSON `null`, caught by the check below.
    const parsed = JSON.parse(raw as string) as
      DeviceInfo | DeviceInfo[] | null;
    if (!parsed) {
      return null;
    }

    // iOS stores device data within an array.
    data =
      platform === DevicePlatform.IOS
        ? (parsed as DeviceInfo[])[0]
        : (parsed as DeviceInfo);
  } catch {
    return null;
  }

  // store device info in memory.
  myDeviceInfo = data;

  return data;
}

/**
 * isMobile returns whether we are running on a native mobile application.
 *
 * @returns {boolean}
 */
export function isMobile() {
  return isiOS() || isAndroid();
}

/**
 * isTWA returns whether we are running in/as a Trusted Web Activity.
 *
 * @returns {boolean}
 */
export function isTWA() {
  return isPlatform(
    DevicePlatform.ANDROID_TWA,
    StorageKey.ANDROID_TWA_DATA,
    StorageKey.ANDROID_TWA_DATA,
  );
}

// store computed value locally so we don't need to recompute it everytime.
let _isSafari: boolean | null = null;

// resetIsSafari is used for testing purposes.
export function resetIsSafari() {
  _isSafari = null;
}

/**
 * isSafari determines if the browser running is Safari.
 */
export function isSafari() {
  if (_isSafari === null) {
    _isSafari = !!(
      navigator.vendor &&
      // Apple/MacOS/Safari
      navigator.vendor.toLowerCase().indexOf('apple') > -1 &&
      navigator.userAgent &&
      // Chrome for iOS
      navigator.userAgent.toLowerCase().indexOf('crios') === -1 &&
      // Firefox for iOS
      navigator.userAgent.toLowerCase().indexOf('fxios') === -1
    );
  }

  return _isSafari;
}

/**
 * isiOS detects if the device we are running on is iOS.
 *
 * @returns {boolean}
 */
export function isiOS() {
  return isPlatform(
    DevicePlatform.IOS,
    StorageKey.IOS_DATA,
    StorageKey.OLD_IOS_DATA,
  );
}

export function isCodeiOS() {
  const rawTokenData = localStorage.getItem(StorageKey.IOS_TOKENS);
  if (!rawTokenData) {
    return false;
  }

  // check token scopes for 'offline_access' which will only be available if we
  // have enabled auth code flow with PKCE.
  const tokenData = JSON.parse(rawTokenData) as { scope?: string };
  // TS-BUG: Returns undefined or '' instead of false when scope is missing or
  // empty.
  return (
    tokenData.scope &&
    tokenData.scope.toLowerCase().split(' ').includes('offline_access')
  );
}

/**
 * isAndroid detects if the device we are running on is an Android.
 *
 * @returns {boolean}
 */
export function isAndroid() {
  return isPlatform(
    DevicePlatform.ANDROID,
    StorageKey.ANDROID_DATA,
    StorageKey.OLD_ANDROID_DATA,
  );
}

/**
 * isPlatform returns whether we are runnning on the provided platform.
 *
 * @param {DevicePlatform} platform
 * @param {StorageKey} storeKey
 * @param {StorageKey} oldStoreKey
 * @returns {boolean}
 */
function isPlatform(
  platform: number,
  storeKey: string,
  oldStoreKey: string,
): boolean {
  if (getPlatform() === platform) {
    // detect platform key.
    return true;
  }

  // As a back up/transitional fallthrough, if we can successfully get device
  // data then we are on the given platform.
  const deviceData =
    localStorage.getItem(storeKey) || localStorage.getItem(oldStoreKey);
  const isPlatform = deviceData !== null;
  if (isPlatform) {
    myPlatform = platform;
    // manually older apps to the platform storage key.
    localStorage.setItem(StorageKey.PLATFORM, String(myPlatform));
  }

  return isPlatform;
}

export { canPersist } from '@linc-technologies/oauth2';
